import { Alert, Button, Tag } from 'antd';
import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { CreditHoldDecisionModal } from './CreditHoldDecisionModal';
import type { JobDetail } from '../../../jobs.api';
import type { useJobCreditHold } from '../hooks/use-job-credit-hold';

interface JobCreditHoldPanelProps {
  job: JobDetail;
  creditHold: ReturnType<typeof useJobCreditHold>;
}

/**
 * Shown on a job's own page whenever it's on a credit hold (gitaumoses4/chukua#37): a second
 * tag alongside the status one, and — only for a manager (`job.approve_credit_hold`) who isn't
 * who flagged it — "Approve" and "Keep holding". Button-hiding is UX only; the route's
 * permission guard plus `isSelfFlagged` remain the real authority, same as `canManage` elsewhere.
 */
export function JobCreditHoldPanel({ job, creditHold }: JobCreditHoldPanelProps) {
  const [decision, setDecision] = useState<'approved' | 'held' | null>(null);
  const { state } = useCurrentUser();

  if (job.status !== 'on_hold' || job.holdReason !== 'credit') return null;

  const canDecide =
    state.status === 'authenticated' && state.user.permissions.includes('job.approve_credit_hold') && state.user.id !== job.creditFlaggedByUserId;

  async function submit(reason: string | undefined) {
    if (!decision) return;
    await creditHold.decide(job, decision === 'approved' ? { decision, reason: reason ?? '' } : { decision, reason });
    setDecision(null);
  }

  return (
    <>
      <Alert
        className="mb-5"
        type="warning"
        showIcon
        message={
          <div className="flex flex-wrap items-center gap-3">
            <Tag color="warning">Credit hold</Tag>
            <span>Flagged over its credit limit or badly overdue — pending a manager decision.</span>
            {canDecide && (
              <div className="ml-auto flex gap-2">
                <Button size="small" loading={creditHold.submitting} onClick={() => setDecision('held')}>
                  Keep holding
                </Button>
                <Button size="small" type="primary" loading={creditHold.submitting} onClick={() => setDecision('approved')}>
                  Approve
                </Button>
              </div>
            )}
          </div>
        }
      />
      <CreditHoldDecisionModal decision={decision} jobNumber={job.number} onCancel={() => setDecision(null)} onSubmit={submit} />
    </>
  );
}
